/**
 * Guardian Consent Screen — Phase 2E
 *
 * Shown during onboarding for users age 13–15 (younger_minor).
 * Requires a parent or guardian to acknowledge usage before proceeding.
 *
 * On confirm:
 *   - Updates profile: guardian_consent_acknowledged_at, minor_compliance_completed,
 *     is_minor_protected = true, visibility = 'private'
 *   - Navigates to next onboarding step (terms-accept)
 *
 * Back/dismiss is intentionally blocked — this is a blocking compliance flow.
 *
 * FUTURE PHASES:
 *   - is_minor_protected=true should trigger: minor-safe UI hiding adult content,
 *     restricting DM/contact features, guardian portal access, deeper data minimization.
 *     For now it enforces visibility=private as the sole active restriction.
 */

import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Check, ShieldCheck } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS } from '@/constants/designTokens';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function GuardianConsentScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { user, refreshProfile } = useAuth();

  const [guardianEmail, setGuardianEmail] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canContinue = acknowledged && !saving;

  const handleConfirm = async () => {
    if (!canContinue || !user?.id) return;

    if (guardianEmail.trim() && !EMAIL_RE.test(guardianEmail.trim())) {
      setError('Please enter a valid guardian email address, or leave it blank.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const now = new Date().toISOString();

      const updates: Record<string, unknown> = {
        guardian_consent_acknowledged_at: now,
        minor_compliance_completed: true,
        /**
         * FUTURE PHASE NOTE:
         * is_minor_protected = true signals that this user is a protected minor (age 13–15).
         * Current enforcement: visibility forced to 'private' below.
         * Future phases should also:
         *   - Hide or blur adult/competitive content
         *   - Restrict messaging/DM features
         *   - Surface minor-safe UI labels and settings
         *   - Provide guardian portal access tied to guardian_email
         *   - Apply deeper data minimization (COPPA follow-up review)
         */
        is_minor_protected: true,
        // Enforce private visibility for protected minors
        visibility: 'private',
      };

      if (guardianEmail.trim()) {
        updates.guardian_email = guardianEmail.trim();
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (updateError) throw updateError;

      await refreshProfile?.();

      // Navigate to next onboarding step — terms acceptance
      router.replace('/(onboarding)/terms-accept');
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.inner}>
            <ShieldCheck size={44} color={c.accent} />

            <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
              PARENT / GUARDIAN CONSENT
            </Text>

            <Text style={[styles.body, { color: c.textSecondary }]}>
              DUEL requires a parent or guardian to acknowledge app usage for users under 16.
            </Text>

            <Text style={[styles.body, { color: c.textSecondary }]}>
              By checking the box below, a parent or guardian confirms they are aware of and consent
              to this account being created and used under their supervision.
            </Text>

            {/* Optional guardian email */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: c.textSecondary }]}>
                Guardian Email <Text style={{ color: c.textTertiary }}>(optional)</Text>
              </Text>
              <TextInput
                style={[
                  styles.input,
                  { backgroundColor: c.surface, borderColor: c.border, color: c.text },
                ]}
                value={guardianEmail}
                onChangeText={(t) => { setGuardianEmail(t); setError(''); }}
                placeholder="parent@example.com"
                placeholderTextColor={c.textTertiary}
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                editable={!saving}
                accessibilityLabel="Guardian email address (optional)"
              />
            </View>

            {/* Acknowledgment checkbox */}
            <Pressable
              style={styles.checkRow}
              onPress={() => { setAcknowledged((v) => !v); setError(''); }}
              disabled={saving}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: acknowledged }}
              accessibilityLabel="Acknowledge guardian consent"
            >
              <View
                style={[
                  styles.checkbox,
                  {
                    borderColor: acknowledged ? c.accent : c.border,
                    backgroundColor: acknowledged ? c.accent : 'transparent',
                  },
                ]}
              >
                {acknowledged && <Check size={16} color={c.accentForeground} strokeWidth={3} />}
              </View>
              <Text style={[styles.checkLabel, { color: c.text }]}>
                I am a parent or guardian and I acknowledge that I have reviewed DUEL's{' '}
                <Text style={{ color: c.accent }}>Terms of Service</Text> and{' '}
                <Text style={{ color: c.accent }}>Privacy Policy</Text>, and I consent to this
                account being used under my supervision.
              </Text>
            </Pressable>

            {error ? (
              <Text style={[styles.error, { color: c.error ?? '#ef4444' }]}>{error}</Text>
            ) : null}

            <Pressable
              style={[
                styles.btn,
                { backgroundColor: c.accent, opacity: canContinue ? 1 : 0.4 },
              ]}
              onPress={handleConfirm}
              disabled={!canContinue}
              accessibilityRole="button"
              accessibilityLabel="Confirm guardian consent and continue"
            >
              {saving ? (
                <ActivityIndicator color={c.accentForeground} />
              ) : (
                <Text style={[styles.btnText, { color: c.accentForeground, fontFamily: TYPO.heading.bold }]}>
                  CONFIRM &amp; CONTINUE
                </Text>
              )}
            </Pressable>

            <Text style={[styles.disclaimer, { color: c.textTertiary }]}>
              This acknowledgment is required to use DUEL for users under 16.
              You cannot skip this step.
            </Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  inner: {
    flex: 1,
    paddingHorizontal: 28,
    paddingTop: 48,
    paddingBottom: 40,
    alignItems: 'center',
    gap: 20,
  },
  title: {
    fontSize: 24,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 8,
  },
  body: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 23,
    maxWidth: 340,
  },
  field: {
    width: '100%',
    gap: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  input: {
    borderWidth: 1,
    borderRadius: RADIUS.xs,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
  },
  checkbox: {
    width: 26,
    height: 26,
    borderWidth: 2,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    flexShrink: 0,
  },
  checkLabel: {
    flex: 1,
    fontSize: 14,
    lineHeight: 22,
  },
  error: {
    fontSize: 13,
    textAlign: 'center',
  },
  btn: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 0,
    alignItems: 'center',
    marginTop: 8,
  },
  btnText: {
    fontSize: 17,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  disclaimer: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
    maxWidth: 300,
  },
});
